/**
 * LCA Engine - ISO 14040/14044 Compliant Life Cycle Assessment
 * Phase 1-2: Goal & Scope + Life Cycle Inventory
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Target, Lock, Unlock, CheckCircle, AlertCircle, Sparkles,
  Database, Zap, Package, TrendingUp, Save, RefreshCw, FileText, BarChart3
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LCAModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [lcaStudies, setLcaStudies] = useState([]);
  const [selectedLCA, setSelectedLCA] = useState(null);
  const [lciData, setLciData] = useState(null);
  const [lciaResults, setLciaResults] = useState(null);
  const [hotspots, setHotspots] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);

  const [newStudyForm, setNewStudyForm] = useState({
    study_name: '',
    functional_unit: '1 MT of Hot Rolled Steel',
    functional_unit_qty: 1.0,
    functional_unit_unit: 'MT',
    system_boundary: 'Cradle-to-Gate',
    reference_flow: 'Hot Rolled Steel Coil',
    intended_application: 'EPD',
    commissioner: '',
    practitioner: ''
  });

  useEffect(() => {
    if (study?.id) {
      fetchLCAStudies();
    }
  }, [study?.id]);

  const fetchLCAStudies = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/lca/studies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studiesForThisERS = res.data.filter(s => s.ers_study_id === study.id);
      setLcaStudies(studiesForThisERS);
      
      if (studiesForThisERS.length > 0) {
        setSelectedLCA(studiesForThisERS[0]);
      }
    } catch (error) {
      console.error('Error fetching LCA studies:', error);
    } finally {
      setLoading(false);
    }
  };

  const createLCAStudy = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/lca/studies`, {
        ...newStudyForm,
        ers_study_id: study.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSuccess('LCA study created successfully!');
      setShowCreateForm(false);
      fetchLCAStudies();
    } catch (error) {
      showError('Error creating LCA study: ' + (error.response?.data?.detail || error.message));
    }
  };

  const lockPhase = async (phase) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/lca/studies/${selectedLCA.id}/phase${phase}/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess(`Phase ${phase} locked successfully!`);
      fetchLCAStudies();
    } catch (error) {
      showError('Error locking phase: ' + (error.response?.data?.detail || error.message));
    }
  };

  const autoPopulateLCI = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/lca/studies/${selectedLCA.id}/lci/auto-populate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLciData(res.data);
      showSuccess(`Auto-populated LCI: ${res.data.summary.electricity_kwh.toLocaleString()} kWh electricity, ${res.data.summary.fuel_types} fuel types`);
    } catch (error) {
      showError('Error auto-populating LCI: ' + (error.response?.data?.detail || error.message));
    }
  };

  const requestAIReview = async () => {
    setGeneratingAI(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/lca/studies/${selectedLCA.id}/phase1/ai-review`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiReview(res.data);
    } catch (error) {
      showError('Error generating AI review: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingAI(false);
    }
  };

  const calculateLCIA = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/lca/studies/${selectedLCA.id}/phase3/calculate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLciaResults(res.data);
      showSuccess(`LCIA Calculated: ${res.data.results.gwp_kg_co2e_per_fu} kg CO2e per ${selectedLCA.functional_unit_unit}`);
    } catch (error) {
      showError('Error calculating LCIA: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchHotspots = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/lca/studies/${selectedLCA.id}/phase4/hotspots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHotspots(res.data);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
    }
  };

  const generateInterpretation = async () => {
    setGeneratingAI(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/lca/studies/${selectedLCA.id}/phase4/ai-insights`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterpretation(res.data);
      setHotspots({ hotspots: res.data.hotspots });
    } catch (error) {
      showError('Error generating interpretation: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingAI(false);
    }
  };

  const getPhaseColor = (status) => {
    if (status === 'locked') return 'bg-green-50 border-green-300 text-green-900';
    if (status === 'in_progress') return 'bg-blue-50 border-blue-300 text-blue-900';
    if (status === 'invalidated') return 'bg-red-50 border-red-300 text-red-900';
    return 'bg-gray-50 border-gray-300 text-gray-600';
  };

  const getPhaseIcon = (status) => {
    if (status === 'locked') return <Lock className="w-5 h-5 text-green-600" />;
    if (status === 'in_progress') return <Unlock className="w-5 h-5 text-blue-600" />;
    return <AlertCircle className="w-5 h-5 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
          <p className="text-gray-600">Loading LCA Engine...</p>
        </div>
      </div>
    );
  }

  // No LCA studies yet - show create form
  if (lcaStudies.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">ISO 14040/14044 LCA Engine</h2>
          <p className="text-teal-100">Life Cycle Assessment with strict phase control and audit compliance</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Target className="w-16 h-16 text-teal-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No LCA Studies Yet</h3>
          <p className="text-gray-600 mb-6">Create your first ISO-compliant Life Cycle Assessment study</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
          >
            Create LCA Study
          </button>
        </div>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-teal-600 px-6 py-4 rounded-t-xl">
                <h3 className="text-xl font-bold text-white">Create LCA Study - Phase 1: Goal & Scope</h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Study Name *</label>
                  <input
                    type="text"
                    value={newStudyForm.study_name}
                    onChange={(e) => setNewStudyForm({...newStudyForm, study_name: e.target.value})}
                    placeholder="e.g., Steel Product LCA 2025"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Functional Unit *</label>
                    <input
                      type="text"
                      value={newStudyForm.functional_unit}
                      onChange={(e) => setNewStudyForm({...newStudyForm, functional_unit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select
                      value={newStudyForm.functional_unit_unit}
                      onChange={(e) => setNewStudyForm({...newStudyForm, functional_unit_unit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="MT">MT (Metric Ton)</option>
                      <option value="kg">kg</option>
                      <option value="unit">Unit</option>
                      <option value="m2">m²</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">System Boundary *</label>
                  <select
                    value={newStudyForm.system_boundary}
                    onChange={(e) => setNewStudyForm({...newStudyForm, system_boundary: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Cradle-to-Gate">Cradle-to-Gate (Raw materials → Factory gate)</option>
                    <option value="Cradle-to-Grave">Cradle-to-Grave (Full life cycle)</option>
                    <option value="Gate-to-Gate">Gate-to-Gate (Manufacturing only)</option>
                    <option value="Cradle-to-Cradle">Cradle-to-Cradle (Including recycling)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Flow *</label>
                  <input
                    type="text"
                    value={newStudyForm.reference_flow}
                    onChange={(e) => setNewStudyForm({...newStudyForm, reference_flow: e.target.value})}
                    placeholder="e.g., Hot Rolled Steel Coil"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intended Application *</label>
                  <select
                    value={newStudyForm.intended_application}
                    onChange={(e) => setNewStudyForm({...newStudyForm, intended_application: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="EPD">EPD (Environmental Product Declaration)</option>
                    <option value="Carbon Footprint">Carbon Footprint (ISO 14067)</option>
                    <option value="CBAM">CBAM Declaration</option>
                    <option value="Internal">Internal Assessment</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createLCAStudy}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Create Study
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main LCA Study View
  const phases = selectedLCA?.phases || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">ISO 14040/14044 LCA Engine</h2>
            <p className="text-teal-100">Life Cycle Assessment: {selectedLCA?.study_name}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-teal-200">Current Phase</div>
            <div className="text-4xl font-bold">Phase {selectedLCA?.current_phase}</div>
            <div className="text-xs text-teal-200">Version {selectedLCA?.current_version}</div>
          </div>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">ISO 14040 Phase Control</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(phaseNum => {
            const phaseData = phases[`phase${phaseNum}`] || {};
            const phaseName = phaseData.phase_name || `Phase ${phaseNum}`;
            const status = phaseData.status || 'not_started';
            
            return (
              <div
                key={phaseNum}
                className={`rounded-xl border-2 p-4 ${getPhaseColor(status)}`}
              >
                <div className="flex items-center justify-between mb-3">
                  {getPhaseIcon(status)}
                  <span className="text-2xl font-bold">P{phaseNum}</span>
                </div>
                <h4 className="font-semibold text-sm mb-1">{phaseName}</h4>
                <p className="text-xs opacity-75 capitalize">{status.replace('_', ' ')}</p>
                {status === 'locked' && phaseData.locked_at && (
                  <p className="text-xs mt-2 opacity-60">
                    Locked: {new Date(phaseData.locked_at).toLocaleDateString()}
                  </p>
                )}
                {status === 'invalidated' && (
                  <p className="text-xs mt-2 font-semibold">Requires re-validation</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase 1: Goal & Scope */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Phase 1: Goal & Scope Definition</h3>
          <div className="flex gap-2">
            <button
              onClick={requestAIReview}
              disabled={generatingAI}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {generatingAI ? 'Reviewing...' : 'AI Review'}
            </button>
            {phases.phase1?.status !== 'locked' && (
              <button
                onClick={() => lockPhase(1)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Lock className="w-4 h-4" />
                Lock Phase 1
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Functional Unit</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedLCA?.functional_unit}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">System Boundary</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedLCA?.system_boundary}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Flow</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedLCA?.reference_flow}
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intended Application</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedLCA?.intended_application}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Linked ERS Study</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                {selectedLCA?.ers_study_name}
              </div>
            </div>
          </div>
        </div>

        {/* AI Review */}
        {aiReview && (
          <div className="mt-6 bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI Review (ISO 14044 Compliance Check)
            </h4>
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown>{aiReview.review}</ReactMarkdown>
              </div>
            <p className="text-xs text-gray-500 mt-3">Generated by {aiReview.model} at {new Date(aiReview.generated_at).toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Phase 2: Life Cycle Inventory */}
      {phases.phase2?.status !== 'not_started' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Phase 2: Life Cycle Inventory (LCI)</h3>
            <div className="flex gap-2">
              <button
                onClick={autoPopulateLCI}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Database className="w-4 h-4" />
                Auto-Populate from ERS/CBAM
              </button>
              {phases.phase2?.status !== 'locked' && (
                <button
                  onClick={() => lockPhase(2)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Lock Phase 2
                </button>
              )}
            </div>
          </div>

          {lciData && (
            <div className="space-y-4">
              {/* Energy Inputs */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  Energy Inputs (Auto-populated from ERS)
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Grid Electricity:</span>
                    <span className="ml-2 font-bold">{lciData.lci_data.energy_inputs.electricity_grid.quantity.toLocaleString()} kWh</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Renewable:</span>
                    <span className="ml-2 font-bold">{lciData.lci_data.energy_inputs.electricity_renewable.quantity.toLocaleString()} kWh</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Renewable Share:</span>
                    <span className="ml-2 font-bold text-green-600">{lciData.summary.renewable_percentage}%</span>
                  </div>
                </div>
              </div>

              {/* Fuel Inputs */}
              {Object.keys(lciData.lci_data.fuel_inputs).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Fuel Inputs (Auto-populated from ERS)</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(lciData.lci_data.fuel_inputs).map(([fuel, data]) => (
                      <div key={fuel} className="flex items-center justify-between">
                        <span className="text-gray-700">{fuel}:</span>
                        <span className="font-bold">{data.quantity.toLocaleString()} {data.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Material Inputs */}
              {lciData.lci_data.material_inputs.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Material Inputs (From CBAM Products)
                  </h4>
                  <div className="space-y-2 text-sm">
                    {lciData.lci_data.material_inputs.map((mat, i) => (
                      <div key={i} className="flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                        <div>
                          <span className="font-medium">{mat.material_name}</span>
                          <span className="text-gray-500 ml-2 text-xs">HS: {mat.hs_code}</span>
                          {mat.process_route && (
                            <div className="text-xs text-gray-500 mt-1">Process: {mat.process_route}</div>
                          )}
                        </div>
                        <div className="text-right">
                          {mat.has_quantity ? (
                            <div>
                              <span className="font-bold text-blue-900">{mat.quantity.toLocaleString()} {mat.unit}</span>
                              <div className="text-xs text-green-600">✓ From CBAM</div>
                            </div>
                          ) : (
                            <span className="text-xs text-orange-600 px-2 py-1 bg-orange-100 rounded">Quantity needed</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}



      {/* Phase 3: LCIA (Impact Calculation) */}
      {phases.phase3?.status !== 'not_started' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Phase 3: Life Cycle Impact Assessment (LCIA)</h3>
            <div className="flex gap-2">
              <button
                onClick={calculateLCIA}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Calculate Impacts
              </button>
              {phases.phase3?.status !== 'locked' && lciaResults && (
                <button
                  onClick={() => lockPhase(3)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Lock Phase 3
                </button>
              )}
            </div>
          </div>

          {lciaResults && (
            <div className="space-y-4">
              {/* Impact Results Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-900 mb-1">Global Warming (GWP)</h4>
                  <p className="text-3xl font-bold text-red-700">
                    {lciaResults.results.gwp_kg_co2e_per_fu.toLocaleString()}
                  </p>
                  <p className="text-xs text-red-600 mt-1">kg CO₂e per {lciaResults.results.functional_unit}</p>
                  <p className="text-sm text-gray-600 mt-2">Total: {lciaResults.results.total_gwp_kg_co2e.toLocaleString()} kg CO₂e</p>
                </div>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-1">Acidification (AP)</h4>
                  <p className="text-3xl font-bold text-yellow-700">
                    {lciaResults.results.ap_kg_so2e_per_fu.toFixed(6)}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">kg SO₂e per {lciaResults.results.functional_unit}</p>
                  <p className="text-sm text-gray-600 mt-2">Total: {lciaResults.results.total_ap_kg_so2e.toFixed(4)} kg SO₂e</p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Eutrophication (EP)</h4>
                  <p className="text-3xl font-bold text-blue-700">
                    {lciaResults.results.ep_kg_po4e_per_fu.toFixed(6)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">kg PO₄e per {lciaResults.results.functional_unit}</p>
                  <p className="text-sm text-gray-600 mt-2">Total: {lciaResults.results.total_ep_kg_po4e.toFixed(4)} kg PO₄e</p>
                </div>
              </div>

              {/* Contribution Breakdown */}
              {lciaResults.breakdown && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">GWP Contribution Breakdown</h4>
                  <div className="space-y-2">
                    {/* Electricity */}
                    {lciaResults.breakdown.electricity && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Grid Electricity</span>
                        <div className="flex items-center gap-3">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-600 h-2 rounded-full"
                              style={{ width: `${lciaResults.breakdown.electricity.contribution_percent}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-900 w-16 text-right">
                            {lciaResults.breakdown.electricity.contribution_percent}%
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Fuels */}
                    {Object.entries(lciaResults.breakdown.fuels || {}).map(([fuel, data]) => (
                      <div key={fuel} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{fuel}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-red-600 h-2 rounded-full"
                              style={{ width: `${data.contribution_percent}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-900 w-16 text-right">
                            {data.contribution_percent}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <strong>✓ Pure Mathematical Calculation</strong> - No AI involvement (ISO 14044 compliant)
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 4: Interpretation */}
      {phases.phase4?.status !== 'not_started' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Phase 4: Interpretation & Hotspot Analysis</h3>
            <div className="flex gap-2">
              <button
                onClick={fetchHotspots}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                Analyze Hotspots
              </button>
              <button
                onClick={generateInterpretation}
                disabled={generatingAI}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {generatingAI ? 'Generating...' : 'AI Insights'}
              </button>
            </div>
          </div>

          {/* Hotspots Table */}
          {hotspots && hotspots.hotspots && (
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Environmental Hotspots (Ranked by Contribution)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Rank</th>
                      <th className="px-4 py-2 text-left">Process</th>
                      <th className="px-4 py-2 text-left">Category</th>
                      <th className="px-4 py-2 text-right">GWP (kg CO₂e)</th>
                      <th className="px-4 py-2 text-right">Contribution</th>
                      <th className="px-4 py-2 text-center">Priority</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hotspots.hotspots.map((h, i) => (
                      <tr key={i} className={i < 3 ? 'bg-red-50' : ''}>
                        <td className="px-4 py-3 font-bold text-gray-900">#{i + 1}</td>
                        <td className="px-4 py-3 font-medium">{h.process}</td>
                        <td className="px-4 py-3">{h.category}</td>
                        <td className="px-4 py-3 text-right font-mono">{h.gwp_kg_co2e.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-orange-600">{h.contribution_percent}%</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            h.priority === 'High' ? 'bg-red-100 text-red-700' :
                            h.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {h.priority}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* AI Interpretation */}
          {interpretation && (
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-6">
              <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                AI Interpretation (ISO 14044 Section 4.4)
              </h4>
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown>{interpretation.insights}</ReactMarkdown>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Generated by {interpretation.model} | {new Date(interpretation.generated_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-teal-600 mt-0.5" />
          <div className="text-sm text-teal-900">
            <p className="font-semibold mb-1">ISO 14040/14044 Compliance</p>
            <p>
              This LCA Engine enforces strict phase locking. Any changes to Phase 1 (Goal & Scope) or Phase 2 (Inventory) 
              will invalidate subsequent results and increment the version number. All operations are logged for audit compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LCAModule;
